import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Upload as UploadIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  ContactMail as ContactMailIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Mouse as MouseIcon,
  Rocket as RocketIcon,
  Send as SendIcon,
  Block as BlockIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  ErrorOutline as ErrorOutlineIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { AuthContext } from './AuthContext';
import { supabase } from './supabaseClient';

const statusColors = {
  new: 'default',
  enriching: 'info',
  enriched: 'primary',
  contacted: 'warning',
  responded: 'success',
  converted: 'success',
  unsubscribed: 'error',
};

const enrichmentColors = {
  pending: 'default',
  in_progress: 'info',
  completed: 'success',
  failed: 'error',
};

function LeadManagementPage() {
  const { currentUser } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [leadContacts, setLeadContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [demoRequests, setDemoRequests] = useState({});
  const [formStats, setFormStats] = useState(null);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderByDir, setOrderByDir] = useState('desc');
  const [formDetailDialog, setFormDetailDialog] = useState({ open: false, text: '' });
  const [importTabValue, setImportTabValue] = useState(0);
  const [manualLeadForm, setManualLeadForm] = useState({ website: '', email: '', phone: '', companyName: '' });
  const [addingManualLead, setAddingManualLead] = useState(false);
  const [manualLeadResult, setManualLeadResult] = useState(null);

  // Handle sort
  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderByDir(orderByDir === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderByDir('asc');
    }
    setPage(0);
  };

  // Fetch emails for a lead
  const fetchEmails = async (leadId) => {
    setLoadingEmails(true);
    try {
      const { data, error } = await supabase
        .from('email_sends')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEmailData(data || []);
    } catch (err) {
      console.error('Error fetching emails:', err);
      setEmailData([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleViewEmails = async () => {
    if (selectedLead) {
      await fetchEmails(selectedLead.id);
      setEmailDialogOpen(true);
    }
    handleMenuClose();
  };

  // Fetch leads
  const fetchLeads = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get company_id from user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('company_id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile?.company_id) {
        setError('No company found. Please set up your company first.');
        setLoading(false);
        return;
      }

      // Apply status filter based on tab
      const statusFilters = {
        0: null, // All
        1: ['new', 'enriching', 'enriched'], // Pipeline
        2: ['contacted'], // Contacted
        3: ['responded', 'converted'], // Engaged
      };

      let query = supabase
        .from('leads')
        .select('*', { count: 'exact' })
        .eq('company_id', profile.company_id)
        .order(orderBy, { ascending: orderByDir === 'asc' })
        .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1);

      if (statusFilters[tabValue]) {
        query = query.in('status', statusFilters[tabValue]);
      }

      if (searchQuery) {
        query = query.or(`website_domain.ilike.%${searchQuery}%,lead_company_name.ilike.%${searchQuery}%`);
      }

      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      // Fetch email status for these leads
      let leadsWithEmailStatus = data || [];
      if (leadsWithEmailStatus.length > 0) {
        const leadIds = leadsWithEmailStatus.map(l => l.id);

        // Batch fetch email status in chunks of 50 to avoid Supabase/Postgres IN clause limits
        const BATCH_SIZE = 50;
        let allEmailData = [];

        for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
          const batchIds = leadIds.slice(i, i + BATCH_SIZE);
          const { data: emailBatch } = await supabase
            .from('email_sends')
            .select('lead_id, status')
            .in('lead_id', batchIds)
            .order('created_at', { ascending: false });
          if (emailBatch) allEmailData = [...allEmailData, ...emailBatch];
        }

        // Create a map of lead_id to latest email status
        const emailStatusMap = {};
        for (const email of allEmailData) {
          // Only keep the first (latest) status for each lead
          if (!emailStatusMap[email.lead_id]) {
            emailStatusMap[email.lead_id] = email.status;
          }
        }

        // Merge email status into leads
        leadsWithEmailStatus = leadsWithEmailStatus.map(lead => ({
          ...lead,
          email_status: emailStatusMap[lead.id] || null
        }));
      }

      // Fetch contact info (email/phone) for these leads
      if (leadsWithEmailStatus.length > 0) {
        const leadIds = leadsWithEmailStatus.map(l => l.id);

        // Batch fetch contacts in chunks of 50 to avoid Supabase/Postgres IN clause limits
        const BATCH_SIZE = 50;
        let allContactsData = [];
        let allFormsData = [];

        for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
          const batchIds = leadIds.slice(i, i + BATCH_SIZE);

          const { data: contactsBatch } = await supabase
            .from('lead_contacts')
            .select('lead_id, email, phone')
            .in('lead_id', batchIds);
          if (contactsBatch) allContactsData = [...allContactsData, ...contactsBatch];

          const { data: formsBatch } = await supabase
            .from('lead_contact_forms')
            .select('lead_id, form_url, has_captcha')
            .in('lead_id', batchIds);
          if (formsBatch) allFormsData = [...allFormsData, ...formsBatch];
        }

        // Create a map of lead_id to contact types
        const contactTypesMap = {};
        for (const contact of allContactsData) {
          if (!contactTypesMap[contact.lead_id]) {
            contactTypesMap[contact.lead_id] = { hasEmail: false, hasPhone: false };
          }
          if (contact.email) contactTypesMap[contact.lead_id].hasEmail = true;
          if (contact.phone) contactTypesMap[contact.lead_id].hasPhone = true;
        }

        // Create a map of lead_id to form info
        const formsMap = {};
        for (const form of allFormsData) {
          formsMap[form.lead_id] = { hasForm: true, formUrl: form.form_url, hasCaptcha: form.has_captcha };
        }

        // Merge contacts and forms into leads
        leadsWithEmailStatus = leadsWithEmailStatus.map(lead => ({
          ...lead,
          contactTypes: contactTypesMap[lead.id] || { hasEmail: false, hasPhone: false },
          formInfo: formsMap[lead.id] || null
        }));
      }

      setLeads(leadsWithEmailStatus);
      setTotalCount(count || 0);

      // Fetch stats
      const { data: statsData } = await supabase
        .rpc('get_lead_stats', { p_company_id: profile.company_id });

      setStats(statsData);

      // Fetch email engagement stats from Mautic API
      try {
        const mauticResponse = await fetch('/api/mautic/email-stats');
        if (mauticResponse.ok) {
          const mauticStats = await mauticResponse.json();

          setEmailStats({
            total_sent: mauticStats.total_sent || 0,
            total_opens: mauticStats.total_opens || 0,
            total_clicks: mauticStats.total_clicks || 0,
            total_unsubscribes: mauticStats.total_unsubscribes || 0,
            demo_requests: mauticStats.demo_requests || 0
          });
        } else {
          console.error('Failed to fetch Mautic stats:', mauticResponse.status);
          setEmailStats({ total_sent: 0, total_opens: 0, total_clicks: 0, total_unsubscribes: 0, demo_requests: 0 });
        }
      } catch (mauticErr) {
        console.error('Error fetching Mautic stats:', mauticErr);
        setEmailStats({ total_sent: 0, total_opens: 0, total_clicks: 0, total_unsubscribes: 0, demo_requests: 0 });
      }

      // Fetch form stats
      try {
        const formResponse = await fetch('/api/mautic/form-stats');
        if (formResponse.ok) {
          const fStats = await formResponse.json();
          setFormStats(fStats);
        }
      } catch (formErr) {
        console.error('Error fetching form stats:', formErr);
      }


      // For Engaged tab, fetch demo request info per lead
      if (tabValue === 3 && leadsWithEmailStatus.length > 0) {
        const leadIds = leadsWithEmailStatus.map(l => l.id);
        const { data: demoData } = await supabase
          .from('email_sends')
          .select('lead_id, clicks, clicked_at')
          .in('lead_id', leadIds)
          .not('clicks', 'is', null);

        const demoMap = {};
        if (demoData) {
          for (const email of demoData) {
            const clicks = email.clicks || [];
            const demoClick = clicks.find(c =>
              c.url && (c.url.includes('demo-request') || c.url.includes('try-demo') || c.url.includes('request-demo'))
            );
            if (demoClick && !demoMap[email.lead_id]) {
              // Extract website from URL
              const urlMatch = demoClick.url.match(/website[=:]([^&]+)/i) || demoClick.url.match(/url[=:]([^&]+)/i);
              let website = urlMatch ? decodeURIComponent(urlMatch[1]) : '';
              if (website) {
                website = website.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
              }
              demoMap[email.lead_id] = {
                website: website || 'Demo Requested',
                date: demoClick.clicked_at || email.clicked_at
              };
            }
          }
        }
        setDemoRequests(demoMap);
      }

    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchLeads();
    }
  }, [currentUser, page, rowsPerPage, tabValue, searchQuery, orderBy, orderByDir]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleMenuClick = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = async () => {
    setDetailsOpen(true);
    handleMenuClose();

    // Fetch contacts for this lead from lead_contacts table
    if (selectedLead?.id) {
      setLoadingContacts(true);
      try {
        const { data: contacts } = await supabase
          .from('lead_contacts')
          .select('*')
          .eq('lead_id', selectedLead.id);
        setLeadContacts(contacts || []);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setLeadContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    }
  };

  const handleSendEmail = async () => {
    // This would trigger the email workflow
    console.log('Sending email to lead:', selectedLead);
    handleMenuClose();
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedLeads.length === 0) return;

    setDeleting(true);
    try {
      // Delete related records from child tables first
      await supabase
        .from('email_sends')
        .delete()
        .in('lead_id', selectedLeads);

      await supabase
        .from('lead_contacts')
        .delete()
        .in('lead_id', selectedLeads);

      await supabase
        .from('lead_contact_forms')
        .delete()
        .in('lead_id', selectedLeads);

      // Now delete the leads
      const { error } = await supabase
        .from('leads')
        .delete()
        .in('id', selectedLeads);

      if (error) throw error;

      setSelectedLeads([]);
      setDeleteDialogOpen(false);
      fetchLeads();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete leads: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (leadId) => {
    try {
      // Delete related records from child tables first
      await supabase
        .from('email_sends')
        .delete()
        .eq('lead_id', leadId);

      await supabase
        .from('lead_contacts')
        .delete()
        .eq('lead_id', leadId);

      await supabase
        .from('lead_contact_forms')
        .delete()
        .eq('lead_id', leadId);

      // Now delete the lead
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;
      fetchLeads();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete lead: ' + err.message);
    }
    handleMenuClose();
  };

  const handleChangeEnrichmentStatus = async (newStatus) => {
    if (selectedLeads.length === 0) return;

    setUpdatingStatus(true);
    try {
      // Build update object based on the type of status change
      const updateData = {
        updated_at: new Date().toISOString()
      };

      // Handle different status types
      if (newStatus === 'enriched') {
        // Re-find contacts: set status='enriched' and enrichment_status='completed'
        updateData.status = 'enriched';
        updateData.enrichment_status = 'completed';
      } else if (newStatus === 'contacts_found' || newStatus === 'no_contacts') {
        // These are main status values, not enrichment_status
        updateData.status = newStatus;
      } else {
        // Default: update enrichment_status
        updateData.enrichment_status = newStatus;
      }

      const { error } = await supabase
        .from('leads')
        .update(updateData)
        .in('id', selectedLeads);

      if (error) throw error;

      // Keep selection after update (removed setSelectedLeads([]))
      fetchLeads();
    } catch (err) {
      console.error('Status update error:', err);
      setError('Failed to update status: ' + err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle bulk email status change
  const handleChangeEmailStatus = async (newStatus) => {
    if (selectedLeads.length === 0) return;

    setUpdatingStatus(true);
    try {
      // First, get which leads have email_sends records
      const { data: existingEmails } = await supabase
        .from('email_sends')
        .select('lead_id')
        .in('lead_id', selectedLeads);

      const leadsWithEmails = existingEmails?.map(e => e.lead_id) || [];

      if (leadsWithEmails.length === 0) {
        setError('None of the selected leads have email records to update.');
        setUpdatingStatus(false);
        return;
      }

      // Handle special case: delete email records to allow re-writing
      if (newStatus === 'delete_emails') {
        const { error } = await supabase
          .from('email_sends')
          .delete()
          .in('lead_id', leadsWithEmails);

        if (error) throw error;

        // Also update lead status to allow re-writing
        await supabase
          .from('leads')
          .update({ status: 'contacts_found' })
          .in('id', leadsWithEmails);
      } else {
        // Update email_sends status (without updated_at since column doesn't exist)
        const { error } = await supabase
          .from('email_sends')
          .update({ status: newStatus })
          .in('lead_id', leadsWithEmails);

        if (error) throw error;
      }

      const updatedCount = leadsWithEmails.length;
      const skippedCount = selectedLeads.length - updatedCount;

      if (skippedCount > 0) {
        setError(`Updated ${updatedCount} leads. ${skippedCount} leads have no email records.`);
      }

      // Selection preserved after update
      fetchLeads();
    } catch (err) {
      console.error('Email status update error:', err);
      setError('Failed to update email status: ' + err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  // Parse BuiltWith CSV format with full data extraction
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    // Parse header to map column indices
    const headerLine = lines[0].toLowerCase();
    const headers = headerLine.split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));

    // Map common BuiltWith column names to our fields
    const colMap = {};
    headers.forEach((h, i) => {
      const hl = h.toLowerCase();
      if (hl.includes('domain') || hl === 'website' || hl === 'url') colMap.domain = i;
      if (hl === 'company' || hl === 'company name') colMap.company = i;
      if (hl === 'vertical' || hl === 'industry') colMap.vertical = i;
      if (hl === 'quantcast' || hl.includes('quantcast')) colMap.quantcast = i;
      if (hl.includes('traffic') && hl.includes('rank')) colMap.trafficRank = i;
      if (hl === 'telephones' || hl === 'phone' || hl === 'phones') colMap.telephones = i;
      if (hl === 'emails' || hl === 'email') colMap.emails = i;
      if (hl === 'people' || hl === 'contacts') colMap.people = i;
      if (hl === 'city') colMap.city = i;
      if (hl === 'state' || hl === 'region') colMap.state = i;
      if (hl === 'country') colMap.country = i;
      if (hl === 'twitter') colMap.twitter = i;
      if (hl === 'facebook') colMap.facebook = i;
      if (hl === 'linkedin') colMap.linkedin = i;
      if (hl.includes('spend') || hl === 'tech spend' || hl.includes('yearly tech')) colMap.techSpend = i;
      if (hl.includes('revenue') || hl === 'sales revenue' || hl === 'sales') colMap.revenue = i;
      if (hl.includes('tech') && !hl.includes('spend')) colMap.technologies = i;
      // New columns for extended BuiltWith data
      if (hl === 'employees' || hl.includes('employee')) colMap.employees = i;
      if (hl === 'followers' || hl.includes('follower')) colMap.followers = i;
      if (hl === 'product' || hl === 'products' || hl.includes('product')) colMap.products = i;
      if (hl === 'sku') colMap.sku = i;
      if (hl === 'zip' || hl === 'postal code' || hl === 'zipcode') colMap.zip = i;
      if (hl === 'tranco') colMap.tranco = i;
      if (hl === 'page rank' || hl === 'pagerank') colMap.pageRank = i;
      if (hl === 'crux rank') colMap.cruxRank = i;
      if (hl === 'cloudflare rank') colMap.cloudflareRank = i;
      if (hl === 'x' || hl === 'x.com') colMap.x = i;
      if (hl === 'tiktok') colMap.tiktok = i;
      if (hl === 'instagram') colMap.instagram = i;
      if (hl === 'youtube') colMap.youtube = i;
      if (hl === 'ecommerce platform' || hl === 'ecommerce') colMap.ecommerce = i;
      if (hl === 'cms platform' || hl === 'cms') colMap.cms = i;
      if (hl === 'crm platform' || hl === 'crm') colMap.crm = i;
      if (hl === 'marketing automation platform' || hl === 'marketing automation') colMap.marketing = i;
      if (hl === 'payment platforms' || hl === 'payments') colMap.payments = i;
      if (hl === 'agency') colMap.agency = i;
      if (hl === 'hosting provider' || hl === 'hosting') colMap.hosting = i;
      if (hl === 'ai') colMap.ai = i;
      if (hl === 'compliance' || hl === 'exclusion') colMap.compliance = i;
      if (hl === 'first detected') colMap.firstDetected = i;
      if (hl === 'last found') colMap.lastFound = i;
      if (hl === 'verified profiles') colMap.verifiedProfiles = i;
    });

    // If no domain column found, assume first column
    if (colMap.domain === undefined) colMap.domain = 0;

    const hasHeader = headerLine.includes('domain') || headerLine.includes('company') || headerLine.includes('vertical');
    const startIndex = hasHeader ? 1 : 0;

    const results = [];
    for (let i = startIndex; i < lines.length; i++) {
      // Handle CSV with quoted fields containing commas
      const cols = [];
      let current = '';
      let inQuotes = false;
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          cols.push(current.trim().replace(/^["']|["']$/g, ''));
          current = '';
        } else {
          current += char;
        }
      }
      cols.push(current.trim().replace(/^["']|["']$/g, ''));

      const domainRaw = cols[colMap.domain] || '';
      if (!domainRaw) continue;

      let domain = domainRaw.toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/.*$/, '');

      if (!domain || !domain.includes('.')) continue;

      // Parse tech spend (remove $ and commas)
      let techSpend = null;
      if (colMap.techSpend !== undefined && cols[colMap.techSpend]) {
        const spendStr = cols[colMap.techSpend].replace(/[$,]/g, '');
        techSpend = parseFloat(spendStr) || null;
      }

      // Parse telephones and emails (often semicolon separated)
      const parseList = (val) => {
        if (!val) return [];
        return val.split(/[;|]/).map(v => v.trim()).filter(v => v);
      };

      // Parse numeric values like "€79,999+" or "4,003+" or "1,834+"
      const parseNumeric = (val) => {
        if (!val) return null;
        // Remove currency symbols, commas, +, spaces
        const cleaned = val.replace(/[€$,+\s]/g, '');
        const num = parseInt(cleaned);
        return isNaN(num) ? null : num;
      };

      // Parse sales revenue (handles €407,150,407+ format)
      let salesRevenue = null;
      if (colMap.revenue !== undefined && cols[colMap.revenue]) {
        salesRevenue = parseNumeric(cols[colMap.revenue]);
      }

      results.push({
        website_url: `https://${domain}`,
        website_domain: domain,
        lead_company_name: cols[colMap.company] || null,
        vertical: cols[colMap.vertical] || null,
        quantcast_rank: colMap.quantcast !== undefined ? parseInt(cols[colMap.quantcast]) || null : null,
        traffic_rank: colMap.trafficRank !== undefined ? parseNumeric(cols[colMap.trafficRank]) : null,
        tech_spend: techSpend,
        sales_revenue: salesRevenue,
        employee_count: colMap.employees !== undefined ? parseNumeric(cols[colMap.employees]) : null,
        social_followers: colMap.followers !== undefined ? parseNumeric(cols[colMap.followers]) : null,
        product_count: colMap.products !== undefined ? parseNumeric(cols[colMap.products]) : null,
        city: cols[colMap.city] || null,
        state: cols[colMap.state] || null,
        country: cols[colMap.country] || null,
        builtwith_telephones: parseList(cols[colMap.telephones]),
        builtwith_emails: parseList(cols[colMap.emails]),
        builtwith_people: cols[colMap.people] ? parseList(cols[colMap.people]) : [],
        social_twitter: cols[colMap.twitter] || null,
        social_facebook: cols[colMap.facebook] || null,
        social_linkedin: cols[colMap.linkedin] || null,
        technologies_list: cols[colMap.technologies] ? parseList(cols[colMap.technologies]) : [],
        import_source: 'builtwith',
        // New extended BuiltWith fields
        postal_code: cols[colMap.zip] || null,
        bw_sku_count: colMap.sku !== undefined ? parseNumeric(cols[colMap.sku]) : null,
        tranco_rank: colMap.tranco !== undefined ? parseNumeric(cols[colMap.tranco]) : null,
        page_rank: colMap.pageRank !== undefined ? parseNumeric(cols[colMap.pageRank]) : null,
        crux_rank: cols[colMap.cruxRank] || null,
        cloudflare_rank: cols[colMap.cloudflareRank] || null,
        social_x: cols[colMap.x] || null,
        social_tiktok: cols[colMap.tiktok] || null,
        social_instagram: cols[colMap.instagram] || null,
        social_youtube: cols[colMap.youtube] || null,
        ecommerce_platform: cols[colMap.ecommerce] || null,
        cms_platform: cols[colMap.cms] || null,
        crm_platform: cols[colMap.crm] || null,
        marketing_platform: cols[colMap.marketing] || null,
        payment_platforms: cols[colMap.payments] ? parseList(cols[colMap.payments]) : [],
        agency: cols[colMap.agency] || null,
        hosting_provider: cols[colMap.hosting] || null,
        has_ai: cols[colMap.ai] || null,
        compliance: cols[colMap.compliance] || null,
        bw_first_detected: cols[colMap.firstDetected] || null,
        bw_last_found: cols[colMap.lastFound] || null,
        bw_verified_profiles: cols[colMap.verifiedProfiles] ? parseList(cols[colMap.verifiedProfiles]) : []
      });
    }
    return results;
  };

  const handleManualAdd = async () => {
    const { website, email, phone, companyName } = manualLeadForm;
    if (!website && !email) {
      setManualLeadResult({ success: false, message: 'Please provide at least a website or email address.' });
      return;
    }

    setAddingManualLead(true);
    setManualLeadResult(null);

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('company_id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('No company found');
      }

      // Parse domain from website
      let domain = website.trim().toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/.*$/, '');

      // If no website provided but email is, extract domain from email
      if (!domain && email) {
        const emailParts = email.split('@');
        if (emailParts.length === 2) {
          domain = emailParts[1].toLowerCase();
        }
      }

      if (!domain || !domain.includes('.')) {
        throw new Error('Please provide a valid website URL or email address.');
      }

      // Check if lead already exists
      const { data: existingLead } = await supabase
        .from('leads')
        .select('id')
        .eq('company_id', profile.company_id)
        .eq('website_domain', domain)
        .single();

      if (existingLead) {
        // Update existing lead with new contact info
        if (email || phone) {
          await supabase.from('lead_contacts').insert({
            lead_id: existingLead.id,
            email: email || null,
            phone: phone || null,
            source: 'manual',
          });
        }
        if (companyName) {
          await supabase.from('leads').update({ lead_company_name: companyName }).eq('id', existingLead.id);
        }
        setManualLeadResult({ success: true, message: `Lead for ${domain} already exists — contact info updated.` });
      } else {
        // Insert new lead
        const { data: newLead, error: insertError } = await supabase
          .from('leads')
          .insert({
            company_id: profile.company_id,
            website_url: `https://${domain}`,
            website_domain: domain,
            lead_company_name: companyName || null,
            import_source: 'manual',
            status: 'new',
            enrichment_status: 'pending',
          })
          .select()
          .single();

        if (insertError) throw insertError;

        // Insert contact info if provided
        if (email || phone) {
          await supabase.from('lead_contacts').insert({
            lead_id: newLead.id,
            email: email || null,
            phone: phone || null,
            source: 'manual',
          });
        }
        setManualLeadResult({ success: true, message: `Lead for ${domain} added successfully!` });
      }

      setManualLeadForm({ website: '', email: '', phone: '', companyName: '' });
      fetchLeads();
    } catch (err) {
      console.error('Manual add error:', err);
      setManualLeadResult({ success: false, message: err.message || 'Failed to add lead.' });
    } finally {
      setAddingManualLead(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setImportResult(null);

    try {
      // Get company_id
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('company_id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile?.company_id) {
        throw new Error('No company found');
      }

      // Read and parse CSV
      const text = await selectedFile.text();
      const leads = parseCSV(text);

      if (leads.length === 0) {
        throw new Error('No valid domains found in CSV');
      }

      // Create lead source
      const { data: source, error: sourceError } = await supabase
        .from('lead_sources')
        .insert({
          company_id: profile.company_id,
          name: `CSV Import - ${selectedFile.name}`,
          source_type: 'csv_upload',
          total_leads: leads.length,
          status: 'processing'
        })
        .select()
        .single();

      if (sourceError) throw sourceError;

      // Insert leads with UPSERT logic (merge on conflict)
      let imported = 0;
      let updated = 0;

      for (const lead of leads) {
        // First check if lead exists
        const { data: existingLead } = await supabase
          .from('leads')
          .select('id')
          .eq('company_id', profile.company_id)
          .eq('website_domain', lead.website_domain)
          .single();

        if (existingLead) {
          // Lead exists - UPDATE with new data (only overwrite if new data exists)
          const updateData = {};
          if (lead.lead_company_name) updateData.lead_company_name = lead.lead_company_name;
          if (lead.vertical) {
            updateData.vertical = lead.vertical;
            updateData.industry = lead.vertical;
          }
          if (lead.tech_spend !== null && lead.tech_spend !== undefined) updateData.tech_spend = lead.tech_spend;
          if (lead.sales_revenue) updateData.sales_revenue = lead.sales_revenue;
          if (lead.quantcast_rank) updateData.quantcast_rank = lead.quantcast_rank;
          if (lead.city) updateData.city = lead.city;
          if (lead.state) updateData.state = lead.state;
          if (lead.country) updateData.country = lead.country;
          if (lead.builtwith_telephones?.length) updateData.builtwith_telephones = lead.builtwith_telephones;
          if (lead.builtwith_emails?.length) updateData.builtwith_emails = lead.builtwith_emails;
          if (lead.builtwith_people?.length) updateData.builtwith_people = lead.builtwith_people;
          if (lead.social_twitter) updateData.social_twitter = lead.social_twitter;
          if (lead.social_facebook) updateData.social_facebook = lead.social_facebook;
          if (lead.social_linkedin) updateData.social_linkedin = lead.social_linkedin;
          if (lead.technologies_list?.length) updateData.technologies_list = lead.technologies_list;
          // New extended BuiltWith fields for UPDATE
          if (lead.postal_code) updateData.postal_code = lead.postal_code;
          if (lead.bw_sku_count) updateData.bw_sku_count = lead.bw_sku_count;
          if (lead.tranco_rank) updateData.tranco_rank = lead.tranco_rank;
          if (lead.page_rank) updateData.page_rank = lead.page_rank;
          if (lead.crux_rank) updateData.crux_rank = lead.crux_rank;
          if (lead.cloudflare_rank) updateData.cloudflare_rank = lead.cloudflare_rank;
          if (lead.social_x) updateData.social_x = lead.social_x;
          if (lead.social_tiktok) updateData.social_tiktok = lead.social_tiktok;
          if (lead.social_instagram) updateData.social_instagram = lead.social_instagram;
          if (lead.social_youtube) updateData.social_youtube = lead.social_youtube;
          if (lead.ecommerce_platform) updateData.ecommerce_platform = lead.ecommerce_platform;
          if (lead.cms_platform) updateData.cms_platform = lead.cms_platform;
          if (lead.crm_platform) updateData.crm_platform = lead.crm_platform;
          if (lead.marketing_platform) updateData.marketing_platform = lead.marketing_platform;
          if (lead.payment_platforms?.length) updateData.payment_platforms = lead.payment_platforms;
          if (lead.agency) updateData.agency = lead.agency;
          if (lead.hosting_provider) updateData.hosting_provider = lead.hosting_provider;
          if (lead.has_ai) updateData.has_ai = lead.has_ai;
          if (lead.compliance) updateData.compliance = lead.compliance;
          if (lead.bw_first_detected) updateData.bw_first_detected = lead.bw_first_detected;
          if (lead.bw_last_found) updateData.bw_last_found = lead.bw_last_found;
          if (lead.bw_verified_profiles?.length) updateData.bw_verified_profiles = lead.bw_verified_profiles;

          // Only update if there's new data
          if (Object.keys(updateData).length > 0) {
            updateData.source_id = source.id; // Track latest import source

            const { error } = await supabase
              .from('leads')
              .update(updateData)
              .eq('id', existingLead.id);

            if (error) {
              console.error('Update error:', error);
            } else {
              updated++;
            }
          } else {
            updated++; // Count as updated even if no new data
          }
        } else {
          // Lead doesn't exist - INSERT new
          const { error } = await supabase
            .from('leads')
            .insert({
              company_id: profile.company_id,
              source_id: source.id,
              website_url: lead.website_url,
              website_domain: lead.website_domain,
              lead_company_name: lead.lead_company_name,
              vertical: lead.vertical,
              industry: lead.vertical,
              quantcast_rank: lead.quantcast_rank,
              tech_spend: lead.tech_spend,
              sales_revenue: lead.sales_revenue,
              city: lead.city,
              state: lead.state,
              country: lead.country,
              builtwith_telephones: lead.builtwith_telephones,
              builtwith_emails: lead.builtwith_emails,
              builtwith_people: lead.builtwith_people,
              social_twitter: lead.social_twitter,
              social_facebook: lead.social_facebook,
              social_linkedin: lead.social_linkedin,
              technologies_list: lead.technologies_list,
              import_source: lead.import_source || 'manual',
              // New extended BuiltWith fields for INSERT
              postal_code: lead.postal_code,
              bw_sku_count: lead.bw_sku_count,
              tranco_rank: lead.tranco_rank,
              page_rank: lead.page_rank,
              crux_rank: lead.crux_rank,
              cloudflare_rank: lead.cloudflare_rank,
              social_x: lead.social_x,
              social_tiktok: lead.social_tiktok,
              social_instagram: lead.social_instagram,
              social_youtube: lead.social_youtube,
              ecommerce_platform: lead.ecommerce_platform,
              cms_platform: lead.cms_platform,
              crm_platform: lead.crm_platform,
              marketing_platform: lead.marketing_platform,
              payment_platforms: lead.payment_platforms,
              agency: lead.agency,
              hosting_provider: lead.hosting_provider,
              has_ai: lead.has_ai,
              compliance: lead.compliance,
              bw_first_detected: lead.bw_first_detected,
              bw_last_found: lead.bw_last_found,
              bw_verified_profiles: lead.bw_verified_profiles,
              status: 'new',
              enrichment_status: 'pending'
            });

          if (error) {
            console.error('Insert error:', error);
          } else {
            imported++;
          }
        }
      }

      // Update source status
      await supabase
        .from('lead_sources')
        .update({
          status: 'completed',
          enriched_leads: imported
        })
        .eq('id', source.id);

      setImportResult({
        success: true,
        imported,
        updated,
        message: `Successfully processed ${leads.length} leads: ${imported} new, ${updated} updated`
      });

      // Refresh leads list
      fetchLeads();

    } catch (err) {
      console.error('Import error:', err);
      setImportResult({
        success: false,
        message: err.message || 'Import failed'
      });
    } finally {
      setImporting(false);
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%', minWidth: 120 }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" variant="caption" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              {value || 0}
            </Typography>
          </Box>
          <Box sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Lead Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your marketing leads and campaigns
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/leads/linkedin'}
            sx={{
              borderColor: '#0077b5', color: '#0077b5',
              '&:hover': { borderColor: '#005582', bgcolor: 'rgba(0,119,181,0.04)' }
            }}
          >
            🔗 LinkedIn Outreach
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLeads}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setUploadDialogOpen(true); setImportTabValue(1); setManualLeadResult(null); }}
            sx={{ mr: 0 }}
          >
            Add Lead
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => { setUploadDialogOpen(true); setImportTabValue(0); setImportResult(null); }}
          >
            Import Leads
          </Button>
        </Box>
      </Box>

      {/* Stats Cards - compact single row */}
      {stats && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="Total Leads" value={stats.total_leads} icon={<BusinessIcon color="primary" />} color="primary" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="New" value={stats.new_leads} icon={<LanguageIcon color="info" />} color="info" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="Enriched" value={stats.enriched_leads} icon={<PersonIcon color="secondary" />} color="secondary" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="Contacted" value={stats.contacted_leads} icon={<EmailIcon color="warning" />} color="warning" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="Responded" value={stats.responded_leads} icon={<TrendingUpIcon color="success" />} color="success" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="Converted" value={stats.converted_leads} icon={<TrendingUpIcon sx={{ color: '#4caf50' }} />} color="success" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="High Value" value={stats.high_value_leads} icon={<TrendingUpIcon sx={{ color: '#ffc107' }} />} color="warning" />
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
            <StatCard title="With Contacts" value={stats.with_contacts} icon={<ContactMailIcon color="info" />} color="info" />
          </Box>
        </Box>
      )}

      {/* Email Stats Cards - compact single row */}
      {emailStats && (
        <>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Email Engagement
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Emails Sent" value={emailStats.total_sent} icon={<SendIcon color="primary" />} color="primary" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Opens" value={emailStats.total_opens} icon={<VisibilityIcon color="info" />} color="info" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Clicks" value={emailStats.total_clicks} icon={<MouseIcon color="secondary" />} color="secondary" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Demo Requests" value={emailStats.demo_requests} icon={<RocketIcon sx={{ color: '#9c27b0' }} />} color="secondary" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Unsubscribes" value={emailStats.total_unsubscribes || 0} icon={<BlockIcon color="error" />} color="error" />
            </Box>
          </Box>
        </>
      )}

      {/* Form Outreach Stats */}
      {formStats && (
        <>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Form Outreach
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Forms Found" value={formStats.forms_found} icon={<AssignmentIcon color="primary" />} color="primary" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Pending" value={formStats.forms_pending} icon={<PendingActionsIcon color="warning" />} color="warning" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Submitted" value={formStats.forms_submitted} icon={<DescriptionIcon color="info" />} color="info" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Confirmed" value={formStats.forms_confirmed} icon={<CheckCircleIcon color="success" />} color="success" />
            </Box>
            <Box sx={{ flex: '1 1 0', minWidth: 120 }}>
              <StatCard title="Failed" value={formStats.forms_failed} icon={<ErrorOutlineIcon color="error" />} color="error" />
            </Box>
          </Box>
        </>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="All Leads" />
            <Tab label="Pipeline" />
            <Tab label="Contacted" />
            <Tab label="Engaged" />
          </Tabs>
        </Box>

        {/* Search and Delete */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by domain or company name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {selectedLeads.length > 0 && (
            <>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Change Status</InputLabel>
                <Select
                  label="Change Status"
                  value=""
                  onChange={(e) => handleChangeEnrichmentStatus(e.target.value)}
                  disabled={updatingStatus}
                >
                  <MenuItem value="pending">🔄 Pending (Re-crawl)</MenuItem>
                  <MenuItem value="enriched">✨ Enriched (Re-find Contacts)</MenuItem>
                  <MenuItem value="in_progress">⏳ In Progress</MenuItem>
                  <MenuItem value="completed">✅ Completed</MenuItem>
                  <MenuItem value="contacts_found">📞 Contacts Found</MenuItem>
                  <MenuItem value="no_contacts">🚫 No Contacts</MenuItem>
                  <MenuItem value="failed">❌ Failed</MenuItem>
                  <MenuItem value="permanently_failed">🚫 Permanently Failed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Email Status</InputLabel>
                <Select
                  label="Email Status"
                  value=""
                  onChange={(e) => handleChangeEmailStatus(e.target.value)}
                  disabled={updatingStatus}
                >
                  <MenuItem value="delete_emails">🗑️ Delete (Re-write)</MenuItem>
                  <MenuItem value="pending">📨 Pending</MenuItem>
                  <MenuItem value="sent">✅ Sent</MenuItem>
                  <MenuItem value="failed">❌ Failed</MenuItem>
                  <MenuItem value="opened">👀 Opened</MenuItem>
                  <MenuItem value="clicked">🖱️ Clicked</MenuItem>
                  <MenuItem value="bounced">↩️ Bounced</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Delete ({selectedLeads.length})
              </Button>
            </>
          )}
        </Box>

        {/* Loading */}
        {loading && <LinearProgress />}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                    checked={leads.length > 0 && selectedLeads.length === leads.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sortDirection={orderBy === 'lead_company_name' ? orderByDir : false}>
                  <TableSortLabel active={orderBy === 'lead_company_name'} direction={orderBy === 'lead_company_name' ? orderByDir : 'asc'} onClick={() => handleSort('lead_company_name')}>
                    Company
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'website_domain' ? orderByDir : false}>
                  <TableSortLabel active={orderBy === 'website_domain'} direction={orderBy === 'website_domain' ? orderByDir : 'asc'} onClick={() => handleSort('website_domain')}>
                    Website
                  </TableSortLabel>
                </TableCell>
                <TableCell>Industry</TableCell>
                <TableCell>Contacts</TableCell>
                <TableCell>Chat</TableCell>
                <TableCell>Email Status</TableCell>
                <TableCell sortDirection={orderBy === 'status' ? orderByDir : false}>
                  <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? orderByDir : 'asc'} onClick={() => handleSort('status')}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Enrichment</TableCell>
                <TableCell sortDirection={orderBy === 'country' ? orderByDir : false}>
                  <TableSortLabel active={orderBy === 'country'} direction={orderBy === 'country' ? orderByDir : 'asc'} onClick={() => handleSort('country')}>
                    Country
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'created_at' ? orderByDir : false}>
                  <TableSortLabel active={orderBy === 'created_at'} direction={orderBy === 'created_at' ? orderByDir : 'asc'} onClick={() => handleSort('created_at')}>
                    Created
                  </TableSortLabel>
                </TableCell>
                {tabValue === 3 && <TableCell>AI Demo</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id} hover selected={selectedLeads.includes(lead.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {lead.lead_company_name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {lead.company_size || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="a" href={lead.website_url} target="_blank" rel="noopener">
                      {lead.website_domain}
                    </Typography>
                  </TableCell>
                  <TableCell>{lead.industry || lead.vertical || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {lead.contactTypes?.hasEmail && (
                        <Tooltip title="Email found">
                          <Chip label="📧" size="small" color="success" sx={{ minWidth: 28 }} />
                        </Tooltip>
                      )}
                      {lead.contactTypes?.hasPhone && (
                        <Tooltip title="Phone found">
                          <Chip label="📞" size="small" color="info" sx={{ minWidth: 28 }} />
                        </Tooltip>
                      )}
                      {lead.formInfo?.hasForm && (
                        <Tooltip title={lead.formInfo.hasCaptcha ? 'Contact form (has CAPTCHA)' : 'Contact form available'}>
                          <Chip label={lead.formInfo.hasCaptcha ? '📝🔒' : '📝'} size="small" color={lead.formInfo.hasCaptcha ? 'warning' : 'secondary'} sx={{ minWidth: 28 }} />
                        </Tooltip>
                      )}
                      {!lead.contactTypes?.hasEmail && !lead.contactTypes?.hasPhone && !lead.formInfo?.hasForm && '-'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {lead.technologies?.has_chat ? (
                      <Tooltip title={lead.technologies?.chat_widgets || 'Chat detected'}>
                        <Chip label="💬" size="small" color="success" sx={{ minWidth: 32 }} />
                      </Tooltip>
                    ) : (
                      <Chip label="-" size="small" variant="outlined" sx={{ minWidth: 32 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {lead.email_status ? (
                      <Chip
                        label={lead.email_status}
                        size="small"
                        color={lead.email_status === 'sent' ? 'success' : lead.email_status === 'pending' ? 'warning' : lead.email_status === 'failed' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.status}
                      size="small"
                      color={statusColors[lead.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.enrichment_status}
                      size="small"
                      variant="outlined"
                      color={enrichmentColors[lead.enrichment_status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {lead.country || lead.city || '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(lead.created_at).toLocaleDateString()}
                  </TableCell>
                  {tabValue === 3 && (
                    <TableCell>
                      {demoRequests[lead.id] ? (
                        <Tooltip title={`Demo requested on ${new Date(demoRequests[lead.id].date).toLocaleString()}`}>
                          <Chip
                            label={`🚀 ${demoRequests[lead.id].website}`}
                            size="small"
                            color="secondary"
                            sx={{ maxWidth: 150 }}
                          />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, lead)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && leads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tabValue === 3 ? 14 : 13} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No leads found. Import some leads to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={handleViewEmails}>📧 View Emails</MenuItem>
        <MenuItem onClick={handleSendEmail}>Send Email</MenuItem>
        <MenuItem onClick={() => selectedLead && handleDeleteSingle(selectedLead.id)} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Lead Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lead Details</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Company</Typography>
                  <Typography variant="body1">{selectedLead.lead_company_name || 'Unknown'}</Typography>
                </Grid>
                {/* Legal Company Name from contact page / IKAS */}
                {selectedLead.technologies?.legal_company_name && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">🏢 Legal Company Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                      {selectedLead.technologies.legal_company_name}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Website</Typography>
                  <Typography variant="body1" component="a" href={selectedLead.website_url} target="_blank">
                    {selectedLead.website_domain}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Industry</Typography>
                  <Typography variant="body1">{selectedLead.industry || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Size</Typography>
                  <Typography variant="body1">{selectedLead.company_size || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{selectedLead.company_description || '-'}</Typography>
                </Grid>

                {/* Found Contacts from lead_contacts table */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">📋 Found Contacts</Typography>
                  {loadingContacts ? (
                    <Typography variant="body2" color="textSecondary">Loading contacts...</Typography>
                  ) : leadContacts.length > 0 ? (
                    <Box sx={{ mt: 1 }}>
                      {leadContacts.map((contact, idx) => (
                        <Box key={idx} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {(contact.first_name || contact.last_name) && (
                              <Chip
                                label={`👤 ${[contact.first_name, contact.last_name].filter(Boolean).join(' ')}`}
                                size="small"
                                color="primary"
                              />
                            )}
                            {contact.title && (
                              <Chip label={contact.title} size="small" variant="outlined" />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {contact.email && (
                              <Chip
                                label={`📧 ${contact.email}`}
                                size="small"
                                color="success"
                                component="a"
                                href={`mailto:${contact.email}`}
                                clickable
                              />
                            )}
                            {contact.phone && (
                              <Chip
                                label={`📞 ${contact.phone}`}
                                size="small"
                                color="info"
                                component="a"
                                href={`tel:${contact.phone}`}
                                clickable
                              />
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>No contacts found yet</Typography>
                  )}
                </Grid>

                {selectedLead.emails && selectedLead.emails.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">General Company Emails</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {selectedLead.emails.map((email, idx) => (
                        <Chip key={idx} label={email} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {selectedLead.pain_points && selectedLead.pain_points.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Pain Points</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {selectedLead.pain_points.map((point, idx) => (
                        <Chip key={idx} label={point} size="small" color="warning" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Social Media Links */}
                {selectedLead.social_links && Object.values(selectedLead.social_links).some(v => v) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Social Media</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {selectedLead.social_links.linkedin && (
                        <Chip
                          label="LinkedIn"
                          size="small"
                          color="primary"
                          component="a"
                          href={selectedLead.social_links.linkedin}
                          target="_blank"
                          clickable
                        />
                      )}
                      {selectedLead.social_links.facebook && (
                        <Chip
                          label="Facebook"
                          size="small"
                          sx={{ bgcolor: '#1877f2', color: 'white' }}
                          component="a"
                          href={selectedLead.social_links.facebook}
                          target="_blank"
                          clickable
                        />
                      )}
                      {selectedLead.social_links.instagram && (
                        <Chip
                          label="Instagram"
                          size="small"
                          sx={{ bgcolor: '#e4405f', color: 'white' }}
                          component="a"
                          href={selectedLead.social_links.instagram}
                          target="_blank"
                          clickable
                        />
                      )}
                      {selectedLead.social_links.twitter && (
                        <Chip
                          label="Twitter/X"
                          size="small"
                          sx={{ bgcolor: '#000', color: 'white' }}
                          component="a"
                          href={selectedLead.social_links.twitter}
                          target="_blank"
                          clickable
                        />
                      )}
                      {selectedLead.social_links.youtube && (
                        <Chip
                          label="YouTube"
                          size="small"
                          sx={{ bgcolor: '#ff0000', color: 'white' }}
                          component="a"
                          href={selectedLead.social_links.youtube}
                          target="_blank"
                          clickable
                        />
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Chat Widget Detection */}
                {selectedLead.technologies && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Chat Widgets Detected</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                      {selectedLead.technologies.has_chat ? (
                        <>
                          <Chip
                            label={selectedLead.technologies.chat_widgets || 'Chat detected'}
                            size="small"
                            color="success"
                            variant="filled"
                          />
                          <Typography variant="caption" color="success.main">
                            ✓ Has live chat - potential competitor!
                          </Typography>
                        </>
                      ) : (
                        <Chip
                          label="No chat widget"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Phones */}
                {selectedLead.phones && selectedLead.phones.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Phone Numbers</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {selectedLead.phones.map((phone, idx) => (
                        <Chip key={idx} label={phone} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* WhatsApp Numbers */}
                {selectedLead.technologies && selectedLead.technologies.whatsapp_numbers && selectedLead.technologies.whatsapp_numbers.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">WhatsApp Numbers</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {selectedLead.technologies.whatsapp_numbers.map((whatsapp, idx) => (
                        <Chip
                          key={idx}
                          label={whatsapp}
                          size="small"
                          color="success"
                          icon={<span>💬</span>}
                          component="a"
                          href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          clickable
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}


                {/* Address */}
                {selectedLead.technologies && selectedLead.technologies.address && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">📍 Address</Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedLead.technologies.address}
                    </Typography>
                  </Grid>
                )}

                {/* Tech Stack */}
                {selectedLead.tech_stack && (
                  <>
                    {/* CMS */}
                    {selectedLead.tech_stack.cms && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Website Platform (CMS)</Typography>
                        <Chip
                          label={selectedLead.tech_stack.cms}
                          size="small"
                          color="info"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                    )}

                    {/* E-commerce Platform */}
                    {selectedLead.tech_stack.ecommerce && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">E-commerce Platform</Typography>
                        <Chip
                          label={selectedLead.tech_stack.ecommerce}
                          size="small"
                          color="secondary"
                          sx={{ mt: 0.5 }}
                        />
                        {selectedLead.tech_stack.has_ecommerce && (
                          <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                            ✓ E-commerce Active
                          </Typography>
                        )}
                      </Grid>
                    )}

                    {/* Frameworks */}
                    {selectedLead.tech_stack.frameworks && selectedLead.tech_stack.frameworks.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Frameworks & Libraries</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {selectedLead.tech_stack.frameworks.map((fw, idx) => (
                            <Chip key={idx} label={fw} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Analytics */}
                    {selectedLead.tech_stack.analytics && selectedLead.tech_stack.analytics.length > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Analytics Tools</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {selectedLead.tech_stack.analytics.map((tool, idx) => (
                            <Chip key={idx} label={tool} size="small" variant="outlined" color="warning" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Payment */}
                    {selectedLead.tech_stack.payment && selectedLead.tech_stack.payment.length > 0 && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Payment Providers</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {selectedLead.tech_stack.payment.map((provider, idx) => (
                            <Chip key={idx} label={provider} size="small" variant="outlined" color="success" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Content Flags */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Content Features</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {selectedLead.tech_stack.has_blog && (
                          <Chip label="📝 Has Blog" size="small" variant="outlined" />
                        )}
                        {selectedLead.tech_stack.has_pricing && (
                          <Chip label="💰 Has Pricing Page" size="small" variant="outlined" />
                        )}
                        {selectedLead.tech_stack.has_ecommerce && (
                          <Chip label="🛒 E-commerce" size="small" color="secondary" variant="outlined" />
                        )}
                        {selectedLead.tech_stack.is_ssl && (
                          <Chip label="🔒 SSL" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<EmailIcon />} onClick={handleSendEmail}>
            Send Email
          </Button>
        </DialogActions>
      </Dialog>


      {/* Email History Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          📧 Email History
          {selectedLead && (
            <Typography variant="subtitle2" color="textSecondary">
              {selectedLead.lead_company_name || selectedLead.website_domain}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingEmails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : emailData.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No emails have been sent to this lead yet.
            </Alert>
          ) : (
            <Box sx={{ pt: 2 }}>
              {emailData.map((email, index) => (
                <Paper key={email.id} elevation={2} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {email.subject || 'No Subject'}
                    </Typography>
                    <Chip
                      label={email.status}
                      size="small"
                      color={email.status === 'sent' ? 'success' : email.status === 'pending' ? 'warning' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, color: 'text.secondary' }}>
                    <Typography variant="caption">
                      To: {email.to_email}
                    </Typography>
                    <Typography variant="caption">
                      {email.sent_at
                        ? `Sent: ${new Date(email.sent_at).toLocaleString()}`
                        : `Created: ${new Date(email.created_at).toLocaleString()}`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      '& p': { my: 0.5 },
                      '& br': { display: 'block', content: '""', my: 0.5 }
                    }}
                    dangerouslySetInnerHTML={{ __html: email.body_html || email.body_text || 'No content' }}
                  />
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Leads</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSelected}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import / Add Lead Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => { setUploadDialogOpen(false); setSelectedFile(null); setImportResult(null); setManualLeadResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          {importTabValue === 0 ? 'Import Leads' : 'Add Lead Manually'}
        </DialogTitle>
        <Tabs
          value={importTabValue}
          onChange={(e, v) => { setImportTabValue(v); setImportResult(null); setManualLeadResult(null); }}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="CSV Import" icon={<UploadIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Manual Entry" icon={<AddIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
        <DialogContent>
          {/* CSV Import Tab */}
          {importTabValue === 0 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="textSecondary" paragraph>
                Upload a CSV file with website URLs to import leads. The system will automatically enrich each lead with company information.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Expected CSV format: Domain, Category, Technologies (BuiltWith export)
              </Alert>

              <Button variant="outlined" component="label" fullWidth>
                {selectedFile ? selectedFile.name : 'Select CSV File'}
                <input type="file" hidden accept=".csv" onChange={handleFileSelect} />
              </Button>

              {selectedFile && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Ready to import: {selectedFile.name}
                </Typography>
              )}

              {importResult && (
                <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {importResult.message}
                </Alert>
              )}

              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                Or use n8n workflow for advanced import options
              </Typography>
            </Box>
          )}

          {/* Manual Entry Tab */}
          {importTabValue === 1 && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Manually add a single lead by providing their details below.
              </Typography>
              <TextField
                label="Website"
                placeholder="e.g. example.com"
                value={manualLeadForm.website}
                onChange={(e) => setManualLeadForm(prev => ({ ...prev, website: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LanguageIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Email"
                placeholder="e.g. contact@example.com"
                type="email"
                value={manualLeadForm.email}
                onChange={(e) => setManualLeadForm(prev => ({ ...prev, email: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Phone"
                placeholder="e.g. +1 555 123 4567"
                value={manualLeadForm.phone}
                onChange={(e) => setManualLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Company Name"
                placeholder="e.g. Acme Corp"
                value={manualLeadForm.companyName}
                onChange={(e) => setManualLeadForm(prev => ({ ...prev, companyName: e.target.value }))}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BusinessIcon fontSize="small" /></InputAdornment>,
                }}
              />

              {manualLeadResult && (
                <Alert severity={manualLeadResult.success ? 'success' : 'error'}>
                  {manualLeadResult.message}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedFile(null); setImportResult(null); setManualLeadResult(null); }}>
            {(importResult?.success || manualLeadResult?.success) ? 'Close' : 'Cancel'}
          </Button>
          {importTabValue === 0 ? (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!selectedFile || importing}
            >
              {importing ? <CircularProgress size={20} /> : 'Import'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleManualAdd}
              disabled={addingManualLead || (!manualLeadForm.website && !manualLeadForm.email)}
              startIcon={addingManualLead ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {addingManualLead ? 'Adding...' : 'Add Lead'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Form Detail Dialog */}
      <Dialog
        open={formDetailDialog.open}
        onClose={() => setFormDetailDialog({ open: false, text: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Post-Submit Page Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {formDetailDialog.text || 'No content captured yet.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDetailDialog({ open: false, text: '' })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default LeadManagementPage;

